import React, { useEffect, useMemo, useRef, useState } from "react";
import { hero as heroMock, services as servicesMock, faqs, stats as statsMock } from "../mock";
import { getSettings, defaultPremium, defaultThemeColors } from "../utils/storage";
import { Header } from "../components/Header";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Separator } from "../components/ui/separator";
import { Progress } from "../components/ui/progress";
import { Toaster, toast } from "sonner";
import { ArrowDown, Star, Zap } from "lucide-react";
import { ServiceCard } from "../components/ServiceCard";

const ServicePill = ({ text, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1 rounded-full border text-xs transition-colors ${
      active ? "bg-yellow-400/20 border-yellow-400/40 text-yellow-300" : "bg-white/10 border-white/15 text-white/80"
    }`}
  >
    {text}
  </button>
);

export default function Home() {
  const [settings, setSettings] = useState(() => ({
    contact: { whatsapp: heroMock.whatsapp, instagram: heroMock.instagram },
    stats: { entregues: statsMock[0].value, anos: statsMock[1].value, clientes: statsMock[2].value },
    services: servicesMock,
    premium: defaultPremium,
    themeColors: defaultThemeColors,
  }));

  // Load settings from backend on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Add timeout to prevent infinite loading
        const controller = new AbortController();
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/config/load.php`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setSettings(data);
      } catch (error) {
        console.error("Failed to load settings from backend, keeping defaults:", error);
        // Keep default settings if backend fails
      }
    };

    loadSettings();
  }, []);

  const stats = settings?.stats || { entregues: statsMock[0].value, anos: statsMock[1].value, clientes: statsMock[2].value };
  const contact = settings?.contact || { whatsapp: heroMock.whatsapp, instagram: heroMock.instagram };
  const services = (settings?.services?.length ? settings.services : servicesMock);
  const premium = settings?.premium || defaultPremium;

  const [activeTheme, setActiveTheme] = useState("hero");
  const containerRef = useRef(null);
  const [selectedPremium, setSelectedPremium] = useState(Object.keys(premium)[0] || "Neon/LED");

  const themes = useMemo(() => {
    const colors = settings?.themeColors || defaultThemeColors;
    return {
      hero: { bg: colors.hero, overlay: "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.4))" },
      services: { bg: colors.services, overlay: "linear-gradient(180deg, rgba(250,204,21,0.06), rgba(0,0,0,0.2))" },
      destaques: { bg: colors.destaques, overlay: "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(15,37,69,0.6))" },
      faq: { bg: colors.faq, overlay: "linear-gradient(180deg, rgba(0,0,0,0.3), rgba(255,255,255,0.03))" },
      contato: { bg: colors.contato, overlay: "linear-gradient(180deg, rgba(0,0,0,0.1), rgba(0,0,0,0.2))" },
    };
  }, [settings]);

  useEffect(() => {
    const sections = document.querySelectorAll("section[data-theme]");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveTheme(entry.target.getAttribute("data-theme") || "hero");
          }
        });
      },
      { threshold: 0.5 }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const t = themes[activeTheme] || themes.hero;
    if (containerRef.current) {
      containerRef.current.style.setProperty("background", `${t.overlay}, ${t.bg}`);
    }
  }, [activeTheme, themes]);

  useEffect(() => {
    // reload settings if user saved on config page and returned
    const onFocus = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);

                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/config/load.php`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setSettings(data);
      } catch (error) {
        console.error("Failed to reload settings on focus:", error);
        // Don't update settings on focus failure to avoid overriding working data
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const handleQuote = () => {
    toast.success("Pedido recebido! Responderemos em instantes (simulação).", { description: "Atualize o WhatsApp/Instagram no botão de contato." });
  };

  const prazoCfg = premium[selectedPremium] || { value: 85, label: "Ágil" };

  return (
    <div ref={containerRef} className="min-h-screen text-white transition-colors duration-700" id="top">
      <Toaster richColors position="top-right" />
      <Header onNavClick={() => {}} whatsapp={contact.whatsapp} instagram={contact.instagram} />

      {/* HERO */}
      <section id="hero" data-theme="hero" className="relative min-h-[90vh] scroll-mt-28 flex items-center py-24">
        <div className="absolute inset-0 -z-10 transition-colors duration-700" />
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-yellow-400/10 blur-3xl" />
          <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
        </div>
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 items-center gap-10">
            <div>
              <p className="uppercase tracking-widest text-xs text-white/70">Comunicação Visual</p>
              <h1 className="text-4xl md:text-6xl font-extrabold leading-[1.05] mt-3">
                HÉRCULES <span className="text-yellow-400"> Visual</span>
              </h1>
              <p className="text-white/80 mt-4 text-lg max-w-xl">{heroMock.description}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild size="lg" className="bg-yellow-400 text-black hover:bg-yellow-300">
                  <a href="#contato" onClick={handleQuote}><Zap className="w-5 h-5 mr-2" /> Pedir Orçamento</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  <a href="#servicos">Ver Serviços</a>
                </Button>
              </div>
              <div className="mt-8 flex items-center gap-6">
                {[{ label: "Projetos Entregues", value: stats.entregues }, { label: "Anos de Mercado", value: stats.anos }, { label: "Clientes Ativos", value: stats.clientes }].map((s) => (
                  <div key={s.label} className="glass px-5 py-3 rounded-xl border border-white/10">
                    <div className="text-2xl font-extrabold text-yellow-400">{s.value}</div>
                    <div className="text-xs uppercase tracking-wider text-white/70">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-60 h-60 rounded-3xl bg-yellow-400/20 blur-2xl -z-10" />
              <Card className="bg-white/5 backdrop-blur-2xl border-white/10 shadow-2xl">
                <CardHeader>
                  <CardTitle className="text-white">Produção Premium</CardTitle>
                  <CardDescription className="text-white/70">Materiais selecionados, corte a laser, neon e LED com acabamento impecável.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between text-sm text-white/80"><span>Qualidade</span><span>100%</span></div>
                      <Progress value={100} className="h-2 bg-white/10" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-sm text-white/80"><span>Prazo</span><span>{prazoCfg.label}</span></div>
                      <Progress value={prazoCfg.value} className="h-2 bg-white/10" />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {Object.keys(premium).map((pill) => (
                        <ServicePill key={pill} text={pill} active={pill === selectedPremium} onClick={() => setSelectedPremium(pill)} />
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-3xl bg-white/10 blur-2xl -z-10" />
            </div>
          </div>
          <div className="mt-16 flex items-center gap-2 text-white/70"><ArrowDown className="w-5 h-5 animate-bounce" /> role para ver mais</div>
        </div>
      </section>

      {/* SERVIÇOS */}
      <section id="servicos" data-theme="services" className="relative min-h-[90vh] scroll-mt-28 flex items-center py-24">
        <div className="absolute inset-0 -z-10 transition-colors duration-700" />
        <div className="absolute inset-0 -z-10 pointer-events-none"><div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-yellow-400/10 blur-3xl" /><div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-white/10 blur-3xl" /></div>
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <header className="mb-10"><p className="uppercase tracking-widest text-xs text-white/60 mb-2">Serviços</p><h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight">Soluções que destacam sua marca</h2></header>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (<ServiceCard key={s.id || i} service={s} />))}
          </div>
        </div>
      </section>

      {/* DESTAQUES */}
      <section id="destaques" data-theme="destaques" className="relative min-h-[70vh] scroll-mt-28 flex items-center py-24">
        <div className="absolute inset-0 -z-10 transition-colors duration-700" />
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <header className="mb-10"><p className="uppercase tracking-widest text-xs text-white/60 mb-2">Por que a Hércules?</p><h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight">Destaques e diferenciais</h2></header>
          <div className="grid md:grid-cols-3 gap-6">
            {[{title:"Neon & LED com brilho perfeito",desc:"Letras iluminadas que chamam atenção 24/7, com eficiência e durabilidade."},{title:"Impressão de alta definição",desc:"Cores fiéis e materiais selecionados para cada necessidade."},{title:"Instalação técnica e garantida",desc:"Equipe própria para instalação segura e dentro das normas."}].map((card, i) => (
              <Card key={i} className="bg-white/5 backdrop-blur-2xl border-white/10"><CardHeader><CardTitle className="flex items-center gap-2 text-white"><Star className="w-5 h-5 text-yellow-400" /> {card.title}</CardTitle><CardDescription className="text-white/70">{card.desc}</CardDescription></CardHeader></Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" data-theme="faq" className="relative min-h-[70vh] scroll-mt-28 flex items-center py-24">
        <div className="absolute inset-0 -z-10 transition-colors duration-700" />
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <header className="mb-10"><p className="uppercase tracking-widest text-xs text-white/60 mb-2">FAQ</p><h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight">Perguntas frequentes</h2></header>
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div>
              <p className="text-white/75">Transparência em cada etapa: orçamento, produção e instalação.</p>
              <Separator className="my-6 bg-white/10" />
              <div className="space-y-3"><div className="text-sm text-white/70">Prazo médio</div><div className="flex gap-2"><div className="w-1/3 h-2 rounded-full bg-yellow-400/60" /><div className="w-1/3 h-2 rounded-full bg-white/20" /><div className="w-1/3 h-2 rounded-full bg-white/20" /></div></div>
            </div>
            <div>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((f, idx) => (<AccordionItem key={idx} value={`item-${idx}`}><AccordionTrigger className="text-left text-white">{f.q}</AccordionTrigger><AccordionContent className="text-white/80">{f.a}</AccordionContent></AccordionItem>))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CONTATO / CTA - sem formulário */}
      <section id="contato" data-theme="contato" className="relative min-h-[50vh] scroll-mt-28 flex items-center py-24">
        <div className="absolute inset-0 -z-10 transition-colors duration-700" />
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <header className="mb-6"><p className="uppercase tracking-widest text-xs text-black/60 mb-2">Contato</p><h2 className="text-3xl md:text-5xl font-extrabold text-black leading-tight">Pronto para transformar sua fachada?</h2></header>
          <div className="text-black">
            <p className="mt-2 text-black/80">Atendimento rápido pelo WhatsApp ou chame no Instagram.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-black text-yellow-400 hover:bg-neutral-900"><a href={contact.whatsapp} target="_blank" rel="noreferrer">WhatsApp</a></Button>
              <Button asChild size="lg" variant="outline" className="border-black/20 text-black hover:bg-black/10"><a href={contact.instagram} target="_blank" rel="noreferrer">Instagram</a></Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 mt-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-white/70 text-sm">© {new Date().getFullYear()} Hércules Comunicação Visual. Todos os direitos reservados.</div>
          <div className="flex gap-4 text-sm text-white/70"><a href="#servicos" className="hover:text-white">Serviços</a><a href="#faq" className="hover:text-white">FAQ</a><a href="#contato" className="hover:text-white">Contato</a></div>
        </div>
      </footer>
    </div>
  );
}
